import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  hybridApi,
  type HybridHint,
  type HybridHintInput,
  type HybridHintKind,
  type HybridPromptGroup,
} from "@/lib/api";
import { dateTime } from "@/lib/format";
import { useClientTable } from "@/lib/table";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  Select,
  SortableTh,
  Spinner,
  Table,
  TableFooter,
  Td,
  Textarea,
  Th,
} from "@/components/ui";
import { useAuth } from "@/app/auth";
import { handoffReason, sourceLabels, sourceTones, useProviders, validationFields } from "@/features/hybrid";

// --- prompts and hints for qwen ------------------------------------------------

// Where qwen gets stuck on a provider's box, it is told why here: every prompt
// the box said in the recent calls, grouped by wording, with what the plan,
// qwen and the guards did on it; and the hints qwen reads about this provider
// after its general knowledge — a free instruction, or a prompt explained with
// what it means and what is right to do. The hints live beside the plan:
// learning never touches them, "Neu lernen" and a rollback keep them.

const meaningLabels: Record<string, string> = {
  number_prompt: "Nummer eingeben",
  pin_prompt: "PIN eingeben",
  main_menu: "Hauptmenü",
  menu: "Menü",
  record_prompt: "nach dem Signalton aufsprechen",
  playback_stored: "gespeicherte Begrüßung wird vorgespielt",
  playback_new: "neue Aufnahme wird vorgespielt",
  save_prompt: "speichern oder neu aufnehmen",
  saved: "gespeichert",
  already_active: "nur noch deaktivieren (schon aktiv)",
  not_saved: "konnte nicht gespeichert werden",
  wrong_pin: "PIN falsch",
  locked: "gesperrt",
  unknown_number: "Nummer unbekannt",
  no_mailbox: "keine Mobilbox",
  bad_key: "Taste ungültig",
  recording_rejected: "Aufnahme abgelehnt",
  new_messages: "neue Nachrichten",
  first_call: "Erstanruf",
  please_wait: "bitte warten",
  goodbye: "Verabschiedung",
  other: "etwas anderes",
};

const hintActions: { id: string; label: string }[] = [
  { id: "", label: "keine Vorgabe" },
  { id: "keys", label: "Taste drücken" },
  { id: "wait", label: "warten, weiter zuhören" },
  { id: "wait_beep", label: "auf den Signalton warten, dann aufsprechen" },
  { id: "play_greeting", label: "sofort aufsprechen (die Box nimmt schon auf)" },
  { id: "done", label: "fertig (die neue Begrüßung ist gespeichert)" },
  { id: "abort", label: "abbrechen (ein Urteil der Box)" },
];

function actionLabel(h: Pick<HybridHint, "action" | "keys">): string {
  if (!h.action) return "";
  if (h.action === "keys") return `Taste ${h.keys ?? ""}`;
  return hintActions.find((a) => a.id === h.action)?.label ?? h.action;
}

/** The line qwen reads of one hint, as the server renders it (domain/hybrid RenderHints). */
function previewLine(in_: HybridHintInput): string {
  const text = in_.text.trim().replace(/\s+/g, " ");
  if (in_.kind === "instruction") return text ? `- ${text}` : "";
  const said = in_.transcript.trim().replace(/\s+/g, " ");
  if (!said) return "";
  const parts: string[] = [];
  if (in_.meaning) parts.push(`bedeutet ${in_.meaning}`);
  if (in_.action) parts.push(`richtig ist action="${in_.action}"${in_.action === "keys" ? `, keys="${in_.keys.trim()}"` : ""}`);
  if (text) parts.push(text);
  // Without a meaning, an action or a sentence the server refuses the hint.
  if (parts.length === 0) return "";
  return `- Ansage »${said}«: ${parts.join("; ")}`;
}

function CardHead({ title, children, actions }: { title: ReactNode; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {children && <div className="mt-0.5 text-sm muted">{children}</div>}
      </div>
      {actions}
    </div>
  );
}

export function PromptsTab() {
  const { me } = useAuth();
  const canEdit = me?.role === "global_admin";
  const [params, setParams] = useSearchParams();
  const providers = useProviders();
  const providerParam = params.get("provider") ?? "";
  const provider = useMemo(() => {
    const list = providers.data ?? [];
    if (list.some((p) => p.key === providerParam)) return providerParam;
    return list[0]?.key ?? "";
  }, [providers.data, providerParam]);
  const setProvider = (key: string) => setParams({ tab: "prompts", provider: key }, { replace: true });
  const [days, setDays] = useState(30);

  const hints = useQuery({
    queryKey: ["hybrid-hints", provider],
    queryFn: () => hybridApi.hints(provider),
    enabled: provider !== "",
  });
  const prompts = useQuery({
    queryKey: ["hybrid-prompts", provider, days],
    queryFn: () => hybridApi.prompts(provider, days),
    enabled: provider !== "",
  });

  const [editing, setEditing] = useState<{ hint?: HybridHint; seed?: Partial<HybridHintInput> } | null>(null);

  if (providers.isLoading) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <Notice>
        Bleibt qwen bei einem Anbieter immer an derselben Stelle hängen, lässt sich das hier erklären: unten stehen alle
        Ansagen, die die Box in den letzten Anrufen gesagt hat, nach Wortlaut gruppiert, mit dem, was Plan, qwen und die
        Schutzregeln daraus gemacht haben. Eine Ansage lässt sich für qwen erklären (was sie bedeutet, was richtig ist),
        und es gibt freie Anweisungen je Anbieter. qwen liest alle aktiven Hinweise bei jedem Anruf dieses Anbieters nach
        seinem Grundwissen. Entscheiden tut qwen weiter selbst, und die Schutzregeln gelten unverändert (keine verbotene
        Taste, die PIN nur einmal, „fertig“ nur mit Zitat der Box).
      </Notice>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Field label="Provider">
            <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {(providers.data ?? []).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.displayName} ({p.key})
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {provider && (
        <HintsCard
          provider={provider}
          canEdit={canEdit}
          list={hints.data?.hints}
          rendered={hints.data?.rendered ?? ""}
          loading={hints.isLoading}
          error={hints.error as Error | null}
          onNew={() => setEditing({ seed: { kind: "instruction" } })}
          onEdit={(h) => setEditing({ hint: h })}
        />
      )}

      {provider && (
        <PromptsCard
          prompts={prompts.data?.prompts}
          calls={prompts.data?.calls ?? 0}
          days={days}
          setDays={setDays}
          loading={prompts.isLoading}
          error={prompts.error as Error | null}
          hints={hints.data?.hints ?? []}
          canEdit={canEdit}
          onExplain={(g) =>
            setEditing({
              seed: {
                kind: "prompt",
                transcript: g.text,
                meaning: g.snippetMeaning ?? "",
                callId: g.examples[0]?.callId,
                session: g.examples[0]?.session,
                useq: g.examples[0]?.useq,
              },
            })
          }
        />
      )}

      {editing && (
        <HintDialog provider={provider} hint={editing.hint} seed={editing.seed} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

// --- the hints -------------------------------------------------------------------

function HintsCard({
  provider,
  canEdit,
  list,
  rendered,
  loading,
  error,
  onNew,
  onEdit,
}: {
  provider: string;
  canEdit: boolean;
  list: HybridHint[] | undefined;
  rendered: string;
  loading: boolean;
  error: Error | null;
  onNew: () => void;
  onEdit: (h: HybridHint) => void;
}) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["hybrid-hints", provider] });
    void qc.invalidateQueries({ queryKey: ["hybrid-prompts", provider] });
  };
  const toggle = useMutation({
    mutationFn: (h: HybridHint) => hybridApi.updateHint(h.id, { ...inputOf(h), enabled: !h.enabled }),
    onSuccess: refresh,
  });
  const [deleting, setDeleting] = useState<HybridHint | null>(null);
  const remove = useMutation({
    mutationFn: (h: HybridHint) => hybridApi.deleteHint(h.id),
    onSuccess: () => {
      setDeleting(null);
      refresh();
    },
  });

  return (
    <Card>
      <CardHead
        title="Hinweise für qwen"
        actions={canEdit ? <Button onClick={onNew}>Anweisung hinzufügen</Button> : undefined}
      >
        Was qwen über diesen Anbieter wissen soll. Eine Ansage erklärt man am einfachsten unten in der Liste oder direkt
        im Gespräch eines Anrufs („Hinweis für qwen“). Abgeschaltete Hinweise liest qwen nicht.
      </CardHead>
      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="p-4">
          <ErrorNote>{error.message}</ErrorNote>
        </div>
      ) : !list || list.length === 0 ? (
        <Empty title="Noch keine Hinweise" hint="qwen liest bei diesem Anbieter nur das allgemeine Grundwissen." />
      ) : (
        <Table dense>
          <thead>
            <tr>
              <Th>Aktiv</Th>
              <Th>Art</Th>
              <Th>Ansage / Anweisung</Th>
              <Th>Bedeutung</Th>
              <Th>Richtig</Th>
              <Th>Von</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {list.map((h) => (
              <tr key={h.id} className={h.enabled ? "" : "opacity-60"}>
                <Td>
                  <input
                    type="checkbox"
                    aria-label="aktiv"
                    checked={h.enabled}
                    disabled={!canEdit || toggle.isPending}
                    onChange={() => toggle.mutate(h)}
                  />
                </Td>
                <Td className="text-xs whitespace-nowrap">{h.kind === "prompt" ? "Ansage erklärt" : "Anweisung"}</Td>
                <Td className="max-w-xl text-xs">
                  {h.kind === "prompt" ? (
                    <>
                      <div>»{h.transcript}«</div>
                      {h.text && <div className="mt-0.5 muted">{h.text}</div>}
                    </>
                  ) : (
                    h.text
                  )}
                  {h.callId ? (
                    <Link to={`/hybrid/calls/${h.callId}`} className="ml-1 text-xs muted hover:underline">
                      (Anruf #{h.callId}
                      {h.useq ? `, #${h.useq}` : ""})
                    </Link>
                  ) : null}
                </Td>
                <Td className="text-xs">{h.meaning ? (meaningLabels[h.meaning] ?? h.meaning) : <span className="muted">—</span>}</Td>
                <Td className="text-xs">{actionLabel(h) || <span className="muted">—</span>}</Td>
                <Td className="text-xs muted whitespace-nowrap">
                  {h.createdByName || "—"}
                  <div>{dateTime(h.updatedAt)}</div>
                </Td>
                <Td className="whitespace-nowrap text-right">
                  {canEdit && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(h)}>
                        Bearbeiten
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleting(h)}>
                        Löschen
                      </Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {toggle.error && (
        <div className="px-4 pb-3">
          <ErrorNote>{(toggle.error as Error).message}</ErrorNote>
        </div>
      )}
      {rendered && (
        <details className="border-t px-4 py-3" style={{ borderColor: "var(--border)" }}>
          <summary className="cursor-pointer text-sm font-medium">Was qwen liest</summary>
          <p className="mt-1 text-xs muted">
            Mit diesem Text beginnt bei jedem Anruf dieses Anbieters das Gespräch mit qwen, direkt nach seinen Regeln und
            dem Grundwissen; er wird schon beim Wählen vorgeladen.
          </p>
          <pre className="mt-2 overflow-x-auto text-xs leading-relaxed whitespace-pre-wrap">{rendered}</pre>
        </details>
      )}
      <ConfirmDialog
        open={deleting !== null}
        title="Hinweis löschen"
        message={
          <>
            Der Hinweis wird gelöscht; qwen liest ihn ab dem nächsten Anruf nicht mehr. Nur abschalten geht mit dem Haken
            in der Spalte „Aktiv“.
          </>
        }
        busy={remove.isPending}
        error={remove.error ? (remove.error as Error).message : null}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onClose={() => setDeleting(null)}
      />
    </Card>
  );
}

function inputOf(h: HybridHint): HybridHintInput {
  return {
    kind: h.kind,
    transcript: h.transcript ?? "",
    meaning: h.meaning ?? "",
    action: h.action ?? "",
    keys: h.keys ?? "",
    text: h.text ?? "",
    enabled: h.enabled,
    callId: h.callId,
    session: h.session,
    useq: h.useq,
  };
}

// --- the builder -------------------------------------------------------------------

/**
 * HintDialog writes a hint for qwen: a free instruction, or a prompt of the box
 * explained — what it means, what is right to do on it, a sentence of one's own.
 */
export function HintDialog({
  provider,
  hint,
  seed,
  onClose,
}: {
  provider: string;
  hint?: HybridHint;
  seed?: Partial<HybridHintInput>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<HybridHintInput>(() => ({
    kind: "prompt",
    transcript: "",
    meaning: "",
    action: "",
    keys: "",
    text: "",
    enabled: true,
    ...(hint ? inputOf(hint) : seed),
  }));
  const set = <K extends keyof HybridHintInput>(k: K, v: HybridHintInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, keys: form.action === "keys" ? form.keys.trim() : "" };
      return hint ? hybridApi.updateHint(hint.id, body) : hybridApi.createHint(provider, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["hybrid-hints", provider] });
      void qc.invalidateQueries({ queryKey: ["hybrid-prompts", provider] });
      onClose();
    },
  });
  const fields = validationFields(save.error) ?? {};
  const preview = previewLine({ ...form, keys: form.action === "keys" ? form.keys : "" });

  return (
    <Dialog open onClose={onClose} title={hint ? "Hinweis für qwen bearbeiten" : "Hinweis für qwen"} size="lg">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Art" error={fields.kind}>
          <Select value={form.kind} onChange={(e) => set("kind", e.target.value as HybridHintKind)}>
            <option value="prompt">Ansage erklären</option>
            <option value="instruction">Anweisung für diesen Anbieter</option>
          </Select>
        </Field>
        {form.kind === "prompt" && (
          <>
            <Field
              label="Ansage der Box"
              hint="So, wie Whisper sie gehört hat. Auf die Worte der Box kürzen, auf die es ankommt; keine vorgespielte Begrüßung eines Kunden, keine Nummern (qwen liest das bei jedem Anruf)."
              error={fields.transcript}
            >
              <Textarea rows={3} value={form.transcript} onChange={(e) => set("transcript", e.target.value)} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Bedeutung" error={fields.meaning}>
                <Select value={form.meaning} onChange={(e) => set("meaning", e.target.value)}>
                  <option value="">keine Angabe</option>
                  {Object.entries(meaningLabels).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label} ({id})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Richtig ist" error={fields.action}>
                <Select value={form.action} onChange={(e) => set("action", e.target.value)}>
                  {hintActions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {form.action === "keys" && (
              <div className="w-48">
                <Field label="Taste" hint="0–9, *, # oder NUMBER#, PIN#, PIN" error={fields.keys}>
                  <Input value={form.keys} onChange={(e) => set("keys", e.target.value)} />
                </Field>
              </div>
            )}
          </>
        )}
        <Field
          label={form.kind === "prompt" ? "Dazu für qwen (optional)" : "Anweisung"}
          hint={
            form.kind === "prompt"
              ? "Ein Satz, warum, z. B. „die 3 führt bei diesem Anbieter zur persönlichen Begrüßung“."
              : "Was qwen bei diesem Anbieter wissen muss, in einem oder zwei Sätzen."
          }
          error={fields.text}
        >
          <Textarea rows={2} value={form.text} onChange={(e) => set("text", e.target.value)} />
        </Field>
        <Checkbox label="aktiv — qwen liest den Hinweis ab dem nächsten Anruf" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
        {preview && (
          <div>
            <div className="text-xs font-medium muted">So liest qwen den Hinweis</div>
            <pre className="mt-1 overflow-x-auto rounded-md bg-[var(--surface-sunken)] px-3 py-2 text-xs whitespace-pre-wrap">
              {preview}
            </pre>
          </div>
        )}
        {save.error && !Object.keys(fields).length && <ErrorNote>{(save.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            Speichern
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// --- every prompt of the box -------------------------------------------------

type PromptFilter = "all" | "asked" | "stuck" | "unexplained";

function PromptsCard({
  prompts,
  calls,
  days,
  setDays,
  loading,
  error,
  hints,
  canEdit,
  onExplain,
}: {
  prompts: HybridPromptGroup[] | undefined;
  calls: number;
  days: number;
  setDays: (d: number) => void;
  loading: boolean;
  error: Error | null;
  hints: HybridHint[];
  canEdit: boolean;
  onExplain: (g: HybridPromptGroup) => void;
}) {
  const [filter, setFilter] = useState<PromptFilter>("all");
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (prompts ?? []).filter((g) => {
      if (filter === "asked" && g.asked === 0) return false;
      if (filter === "stuck" && !g.stuck) return false;
      if (filter === "unexplained" && (g.hints.length > 0 || g.asked === 0)) return false;
      return !needle || g.variants.some((v) => v.text.toLowerCase().includes(needle)) || (g.snippet ?? "").includes(needle);
    });
  }, [prompts, filter, q]);
  const table = useClientTable(shown, {
    initial: { key: "heard", dir: "desc" },
    pageSize: 50,
    resetOn: [filter, q],
    accessors: { problems: (g) => (g.stuck ? 1000 : 0) + g.refused + Object.keys(g.handoffs).length },
  });
  const hintById = useMemo(() => new Map(hints.map((h) => [h.id, h])), [hints]);

  return (
    <Card>
      <CardHead title="Alle Ansagen">
        Was die Box in den letzten {days} Tagen gesagt hat ({calls} Anrufe), nach Wortlaut gruppiert: wie oft, woran der
        Plan sie erkannt hat, wer entschieden hat und was getan wurde. „hing“ heißt: qwen musste übernehmen und kam nicht
        glatt weiter (der Plan übergab, eine Antwort wurde verworfen, oder der Anruf scheiterte).
      </CardHead>
      <div className="flex flex-wrap items-end gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border)" }}>
        <div className="w-56">
          <Field label="Zeigen">
            <Select value={filter} onChange={(e) => setFilter(e.target.value as PromptFilter)}>
              <option value="all">alle Ansagen</option>
              <option value="asked">nur wo qwen gefragt wurde</option>
              <option value="stuck">nur wo qwen hing</option>
              <option value="unexplained">qwen gefragt, ohne Hinweis</option>
            </Select>
          </Field>
        </div>
        <div className="w-40">
          <Field label="Zeitraum">
            <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 Tage</option>
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
              <option value={365}>ein Jahr</option>
            </Select>
          </Field>
        </div>
        <div className="w-64">
          <Field label="Suche">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Wortlaut oder Schnipsel …" />
          </Field>
        </div>
      </div>
      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="p-4">
          <ErrorNote>{error.message}</ErrorNote>
        </div>
      ) : shown.length === 0 ? (
        <Empty title="Keine Ansage" hint={prompts?.length ? "Der Filter lässt nichts übrig." : "In diesem Zeitraum gab es keinen Anruf."} />
      ) : (
        <>
          <Table dense>
            <thead>
              <tr>
                <Th>Ansage</Th>
                <SortableTh table={table} sortKey="heard" first="desc" align="right">
                  Gehört
                </SortableTh>
                <SortableTh table={table} sortKey="lastAt" first="desc">
                  Zuletzt
                </SortableTh>
                <Th>Erkannt als</Th>
                <Th>Entschieden / getan</Th>
                <SortableTh table={table} sortKey="problems" first="desc">
                  Probleme
                </SortableTh>
                <Th>Hinweis</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {table.rows.map((g) => (
                <PromptRow key={g.key} g={g} hintById={hintById} canEdit={canEdit} onExplain={onExplain} />
              ))}
            </tbody>
          </Table>
          <TableFooter table={table} noun="Ansagen" />
        </>
      )}
    </Card>
  );
}

function PromptRow({
  g,
  hintById,
  canEdit,
  onExplain,
}: {
  g: HybridPromptGroup;
  hintById: Map<number, HybridHint>;
  canEdit: boolean;
  onExplain: (g: HybridPromptGroup) => void;
}) {
  const reasons = Object.entries(g.handoffs).filter(([r]) => r !== "explore");
  return (
    <tr>
      <Td className="max-w-xl text-xs">
        <div className={g.byHead ? "italic" : ""}>{g.text || <span className="muted">(kein Transkript)</span>}</div>
        {g.variants.length > 1 && (
          <details className="mt-1">
            <summary className="cursor-pointer muted">{g.variants.length} Wortlaute</summary>
            <ul className="mt-1 flex flex-col gap-1">
              {g.variants.map((v) => (
                <li key={v.text} className="muted">
                  {v.heard}× »{v.text}«
                </li>
              ))}
            </ul>
          </details>
        )}
      </Td>
      <Td className="text-right text-xs tabular-nums whitespace-nowrap">
        {g.heard}×<div className="muted">{calls(g.calls)}</div>
      </Td>
      <Td className="text-xs muted whitespace-nowrap">
        {dateTime(g.lastAt)}
        <div className="flex flex-wrap gap-1">
          {g.examples.slice(0, 3).map((ex) => (
            <Link key={`${ex.callId}-${ex.useq}`} to={`/hybrid/calls/${ex.callId}`} className="hover:underline">
              #{ex.callId}
            </Link>
          ))}
        </div>
      </Td>
      <Td className="text-xs">
        {g.snippet ? (
          <>
            <span className="font-mono">{g.snippet}</span>
            {g.snippetMeaning && <div className="muted">{meaningLabels[g.snippetMeaning] ?? g.snippetMeaning}</div>}
          </>
        ) : (
          <span className="muted">unbekannt</span>
        )}
        <div className="mt-0.5 flex flex-wrap gap-1">
          {g.byHead && <Badge>am Anfang erkannt</Badge>}
          {g.variable && <Badge tone="warning">Vorspielung</Badge>}
        </div>
      </Td>
      <Td className="text-xs">
        <div className="flex flex-wrap gap-1">
          {Object.entries(g.decidedBy).map(([s, n]) => (
            <Badge key={s} tone={sourceTones[s] ?? "neutral"}>
              {sourceLabels[s] ?? s} {n}×
            </Badge>
          ))}
        </div>
        {Object.keys(g.did).length > 0 && (
          <div className="mt-1 muted">
            {Object.entries(g.did)
              .map(([d, n]) => `${didLabel(d)} ${n}×`)
              .join(" · ")}
          </div>
        )}
      </Td>
      <Td className="text-xs">
        <div className="flex flex-wrap gap-1">
          {g.stuck && <Badge tone="danger">hing</Badge>}
          {g.refused > 0 && <Badge tone="warning">{g.refused}× verworfen</Badge>}
          {g.failedCalls > 0 && <Badge tone="warning">{calls(g.failedCalls)} gescheitert</Badge>}
        </div>
        {reasons.length > 0 && (
          <ul className="mt-1 muted">
            {reasons.map(([r, n]) => (
              <li key={r}>
                {handoffReason(r)} ({n}×)
              </li>
            ))}
          </ul>
        )}
      </Td>
      <Td className="text-xs">
        {g.hints.length === 0 ? (
          <span className="muted">—</span>
        ) : (
          g.hints.map((id) => {
            const h = hintById.get(id);
            return (
              <div key={id} title={h?.text || undefined}>
                <Badge tone={h?.enabled ? "success" : "neutral"}>
                  {h ? [h.meaning && (meaningLabels[h.meaning] ?? h.meaning), actionLabel(h)].filter(Boolean).join(" → ") || "Hinweis" : "Hinweis"}
                  {h && !h.enabled ? " (aus)" : ""}
                </Badge>
              </div>
            );
          })
        )}
      </Td>
      <Td className="text-right whitespace-nowrap">
        {canEdit && (
          <Button size="sm" onClick={() => onExplain(g)}>
            Für qwen erklären
          </Button>
        )}
      </Td>
    </tr>
  );
}

function calls(n: number): string {
  return `${n} ${n === 1 ? "Anruf" : "Anrufe"}`;
}

function didLabel(d: string): string {
  if (d.startsWith("keys ")) return `Taste ${d.slice(5)}`;
  const [action, ...rest] = d.split(" ");
  const label = hintActions.find((a) => a.id === action)?.label.split(" (")[0] ?? action;
  return [label, ...rest].join(" ");
}
